import * as React from 'react';
import * as _ from 'lodash';
import * as Shared from '../shared/shared-types';
import * as CLP from './category-landing-types';
import { CategoryLandingStore } from './category-landing-store';
import { AdService } from '../shared/ad-service';
import { ArticleList } from '../shared/article-list';
import { ArticleListPager } from '../shared/article-list-pager';
import { CheckboxFilterGroup } from '../shared/checkbox-filter-group';
import { RadioFilterGroup } from '../shared/radio-filter-group';
import { AlphabetFilter } from '../shared/alphabet-filter';
import { BodyPartFilter } from '../shared/bodypart-filter';

export class CategoryLandingPage extends React.Component<CLP.ICategoryLandingPageProps, CLP.ICategoryLandingPageState>{

    private _config: CLP.ICategoryLandingConfig;
    private _store: CategoryLandingStore;
    private _isMobileDevice: boolean;
    private _adGutter: HTMLElement;
    private _adService: AdService;
    private _lastAction: string = "lastAction";
    private _lastPage: string = "lastPage";
    private _lastHref: string = "_lastHref";

    constructor(props: CLP.ICategoryLandingPageProps) {

        super(props);
        this._config = props.config;
        this._isMobileDevice = window.innerWidth <= this._config.mobileBreakpoint;

        // Initialize filter state store
        const { pageSize, mobilePageSize } = this._config.articleList;
        this._store = new CategoryLandingStore(this._config.apiEndpoint, (this._isMobileDevice ? mobilePageSize : pageSize), this._config.categoryName);

        // Initialize ad service
        this._adService = new AdService();

        // Set initial component state

        this.state = {
            isLoading: (this._config.inEditMode === 'true') ? false : true,
            mobileFiltersAreOpen: false,
            selectedCategory: CLP.CategoryFilterType.ALL,
            selectedLetter: undefined,
            selectedBodyPart: undefined,
            selectedTopics: new Array<number>(),
            selectedLanguage: ['en'],
            ...this._store.getInitialState()
        };

        // Bind methods
        this.bindMethods();

        // Configure debugging helpers 
        window[this._config.globalPropName] = {};
        window[this._config.globalPropName].lastAction = undefined;
        window[this._config.globalPropName].currentState = this.state;
    }

    componentDidMount() {
        const lastActionJson: string = window.sessionStorage.getItem(this._lastAction);
        const lastAction = JSON.parse(lastActionJson) as CLP.ICategoryLandingFilterAction;
        const lastPage: number = Number(window.sessionStorage.getItem(this._lastPage));
        const lastHref: string = window.sessionStorage.getItem(this._lastHref);

        //url state values
        if (location.search) {
            const { category, bodyPart, topics } = this.getUrlStateValues();
            if (!_.isNil(lastPage)) {
                this.updateState({ category: category, bodyPart: bodyPart, topics: topics, page: lastPage });
            } else {
                this.updateState({ category: category, bodyPart: bodyPart, topics: topics});
            }
        } else if (!_.isNil(lastAction) && lastHref === location.href) {
            this.updateState(lastAction);
        } else {
            window.sessionStorage.clear();
            this.updateState();
        }

        // Set callback to render ads when api is ready
        if (this._config.inEditMode === 'false') {
            this._adService.setRenderCallback(() => this.renderAdBlocks());
        }
    }

    componentDidUpdate() {
        this.smoothScroll();
    }

    get areFiltersApplied(): boolean {
        return this.state.selectedCategory !== CLP.CategoryFilterType.ALL ||
            !!this.state.selectedTopics.length ||
            !!this.state.selectedLanguage;
    }

    //get selectedLanguageArray(): Array<string> {
    //    return _.isNil(this.state.selectedLanguage) ? [] : [this.state.selectedLanguage];
    //}

    private bindMethods(): void {
        this.mobileClickHandler = this.mobileClickHandler.bind(this);
        this.mobileClearHandler = this.mobileClearHandler.bind(this);
        this.categoryChangeHandler = this.categoryChangeHandler.bind(this);
        this.topicsChangeHandler = this.topicsChangeHandler.bind(this);
        this.letterChangeHandler = this.letterChangeHandler.bind(this);
        this.bodyPartChangeHandler = this.bodyPartChangeHandler.bind(this);
        this.nextPageHandler = this.nextPageHandler.bind(this);
        this.prevPageHandler = this.prevPageHandler.bind(this);
        this.pageBtnHandler = this.pageBtnHandler.bind(this);
        this.languageChangeHandler = this.languageChangeHandler.bind(this);
    }

    private updateState(params?: {}): void {

        if (this._config.inEditMode === 'true')
            return;

        // Create action from current state and provided parameters
        const action = _.assign<CLP.ICategoryLandingFilterAction, {}>({
            page: this.state.page,
            category: this.state.selectedCategory,
            letter: this.state.selectedLetter,
            bodyPart: this.state.selectedBodyPart,
            topics: this.state.selectedTopics,
            language: this.state.selectedLanguage
        }, params || {});

        if (action.page === 0) {
            action.page = 1;
        }

        //window[this._config.globalPropName].lastAction = action;
        window.sessionStorage.setItem(this._lastAction, JSON.stringify(action));
        window.sessionStorage.setItem(this._lastPage, action.page.toString());
        window.sessionStorage.setItem(this._lastHref, location.href);


        // Call store to get updated state
        this._store.getUpdatedState(action)
            .then((filterState: CLP.ICategoryLandingFilterState) => {

                this.setState({
                    isLoading: false,
                    selectedCategory: action.category,
                    selectedLetter: action.letter,
                    selectedBodyPart: action.bodyPart,
                    selectedTopics: action.topics,
                    selectedLanguage: action.language,
                    ...filterState
                }, () => window[this._config.globalPropName].currentState = this.state);

            }).catch((error) => console.error(error));

        // Update page to loading state
        this.setState({ isLoading: true });

    }

    private getUrlStateValues(): { category: CLP.CategoryFilterType, bodyPart: number, topics: number[] } {

        const { bodyParts, topics } = this._config.filters;
        let keyValuePairs: string[] = decodeURIComponent(location.search.substring(1)).split('&');

        let hashMap: { [param: string]: string } = {};
        keyValuePairs.forEach(x => {
            const pair: string[] = x.split('=');
            hashMap[pair[0]] = pair[1];
        });

        const bodyPartFilter = _.isNil(hashMap['bodyPart']) ? undefined :
            _.find(bodyParts.filters, x => x.name === hashMap['bodyPart']);

        const topicFilter = _.isNil(hashMap['topic']) ? undefined :
            _.find(topics.filters, x => x.name === hashMap['topic']);

        const bodyPart = (bodyPartFilter !== undefined) ? bodyPartFilter.value : undefined;
        const category = (bodyPart !== undefined) ? CLP.CategoryFilterType.BODYPART : CLP.CategoryFilterType.ALL;
        const topic = (topicFilter !== undefined) ? new Array<number>().concat(topicFilter.value) : new Array<number>();

        return { category: category, bodyPart: bodyPart, topics: topic };

    }

    private renderAdBlocks(): void {
        // Append ad blocks to gutter and refresh
        this._adService.getAdBlocks().forEach(x => this._adGutter.appendChild(x));
        this._adService.refreshAdBlocks();
    }

    public smoothScroll(): void {
        // set an interval to update scrollTop attribute every 25 ms
        var timer = setInterval(function () {
            var pixelJump = 40;

            if (document.documentElement.scrollTop != 0 || document.body.scrollTop != 0) {
                document.documentElement.scrollTop -= pixelJump;
                document.body.scrollTop -= pixelJump;
            }

            // end interval if the scroll is completed
            if (document.documentElement.scrollTop === 0 && document.body.scrollTop === 0) clearInterval(timer);
        }, 25);
    }

    public nextPageHandler(): void {
        // Call into store to page articles list
        if (this.state.page >= this.state.totalPages) return;
        const nextPage = this.state.page + 1;
        this.updateState({ page: nextPage });
    }

    public prevPageHandler(): void {
        // Call into store to page articles list
        if (this.state.page <= 1) return;
        const prevPage = this.state.page - 1;
        this.updateState({ page: prevPage });
    }

    public pageBtnHandler(page: number): void {
        // Call into store to page articles list
        this.updateState({ page: page });
    }

    public categoryChangeHandler(category: number): void {

        // Call into store to filter based on new category
        if (this.state.selectedCategory === category) return;
        let params: {};
        switch (category) {
            case CLP.CategoryFilterType.ALL:
                params = {
                    page: 1,
                    category: CLP.CategoryFilterType.ALL,
                    letter: undefined,
                    bodyPart: undefined
                };
                break;
            case CLP.CategoryFilterType.ALPHABETICAL:
                params = {
                    page: 1,
                    category: CLP.CategoryFilterType.ALPHABETICAL,
                    letter: _.head(this.state.availableLetters),
                    bodyPart: undefined
                };
                break;
            case CLP.CategoryFilterType.BODYPART:
                params = {
                    page: 1,
                    category: CLP.CategoryFilterType.BODYPART,
                    letter: undefined,
                    bodyPart: _.head(this.state.availableBodyParts)
                };
                break;
            default: return;
        }

        this.updateState(params);
    }

    public letterChangeHandler(letter: string): void {

        this.updateState({ page: 1, letter: letter });

    }

    public bodyPartChangeHandler(bodyPart: number): void {

        this.updateState({ page: 1, bodyPart: bodyPart });

    }

    public topicsChangeHandler(topic: number): void {

        let newTopics: number[];
        if (this.state.selectedTopics.indexOf(topic) === -1) {
            newTopics = this.state.selectedTopics.concat(topic);
        } else {
            newTopics = this.state.selectedTopics.filter(x => x !== topic);
        }

        this.updateState({ page: 1, topics: newTopics });

    }

    public languageChangeHandler(language: string): void {

        //if (this.state.selectedLanguage !== language) {
        //    this.updateState({ page: 1, language: language });
        //} else this.updateState({ page: 1, language: undefined });

        const newLang = this.toggleArray(this.state.selectedLanguage, language);
        this.updateState({ page: 1, language: newLang });

    }

    private toggleArray<T>(array: Array<T>, value: T) {

        let newArray: Array<T>;
        if (array.indexOf(value) === -1) {
            newArray = array.concat(value);
        } else {
            newArray = array.filter(x => x !== value);
        }
        return newArray;
    }

    public mobileClickHandler(): void {

        if (this._isMobileDevice) {
            this.setState((prevState: CLP.ICategoryLandingPageState) => {
                return { mobileFiltersAreOpen: !prevState.mobileFiltersAreOpen }
            }, () => document.body.classList.toggle('filtersAreOpen'));
        }

    }

    public mobileClearHandler(): void {

        if (this.areFiltersApplied) {
            this.updateState({
                page: 1,
                category: CLP.CategoryFilterType.ALL,
                letter: undefined,
                bodyPart: undefined,
                topics: new Array<number>(),
                language: undefined
            });
        }

    }

    public getFilterCount(): number {
        let count: number = 0;
        count += this.state.selectedTopics.length;
        if (this.state.selectedCategory != CLP.CategoryFilterType.ALL)
            count += 1;
        return count;
    }

    render() {
        return (
            <div className="category-landing listpage">

                {/* Filter Column */}
                <div className={'listpage-col filters' + (this.state.mobileFiltersAreOpen ? ' filtersAreOpen' : '')}>

                    {/* Filter Column Header */}
                    <div className="filters-header" onClick={this.mobileClickHandler}>
                        <h6 className="filters-header-text">{this._config.filters.filterHeader}</h6>
                        {this._isMobileDevice && this.getFilterCount() > 0 &&
                            <small className="filters-header-count">
                                {this.getFilterCount()}
                            </small>
                        }
                    </div>

                    {/* Filter Groups - Languages, Category & Topics */}
                    <CheckboxFilterGroup
                        name="languages"
                        filterGroup={this._config.filters.languages}
                        appliedValues={this.state.selectedLanguage}
                        availableValues={this.state.availableLanguages}
                        preventDisable={true}
                        onChangeHandler={this.languageChangeHandler} />
                    <RadioFilterGroup
                        name="categories"
                        filterGroup={this._config.filters.categories}
                        selectedValue={this.state.selectedCategory}
                        classes={['filters-category']}
                        onChangeHandler={this.categoryChangeHandler} />
                    <CheckboxFilterGroup
                        name="topics"
                        filterGroup={this._config.filters.topics}
                        classes={['filters-topic']}
                        appliedValues={this.state.selectedTopics}
                        availableValues={this.state.availableTopics}
                        preventDisable={true}
                        onChangeHandler={this.topicsChangeHandler} />

                    {/* Mobile Filter Dialog Controls */}
                    <div className="filters-section filters-controls">
                        <div className={'filters-controls-clear' + (this.areFiltersApplied ? ' canBeCleared' : '')} onClick={this.mobileClearHandler}>
                            {this._config.filters.controls.clearButtonText}
                        </div>
                        <div className="filters-controls-done btn-primary" onClick={this.mobileClickHandler}>
                            {
                                (this.areFiltersApplied) ?
                                    this._config.filters.controls.applyButtonText : this._config.filters.controls.doneButtonText
                            }
                        </div>
                    </div>
                </div>

                {/* Article List Column */}
                <div className="listpage-col list">

                    {/* Category secondary filters - Letters & Body Parts */}
                    {this.state.selectedCategory === CLP.CategoryFilterType.ALL &&
                        <h2 className={'category-landing-list-header ' + this._config.categoryTheme}>
                            {this._config.filters.allContentHeader}
                        </h2>
                    }
                    {this.state.selectedCategory === CLP.CategoryFilterType.ALPHABETICAL &&
                        <AlphabetFilter
                            header={this._config.filters.alphabetHeader}
                            categoryTheme={this._config.categoryTheme}
                            currentSelection={this.state.selectedLetter}
                            availableLetters={this.state.availableLetters}
                            onChangeHandler={this.letterChangeHandler} />
                    }
                    {this.state.selectedCategory === CLP.CategoryFilterType.BODYPART &&
                        <BodyPartFilter
                            categoryTheme={this._config.categoryTheme}
                            filterGroup={this._config.filters.bodyParts}
                            currentSelection={this.state.selectedBodyPart}
                            availableBodyParts={this.state.availableBodyParts}
                            onChangeHandler={this.bodyPartChangeHandler} />
                    }

                    {/* Article List */}
                    <ArticleList
                        isLoading={this.state.isLoading}
                        articles={this.state.articles}
                        noResultMessage={this._config.articleList.noResultMessage} />

                    {/* Article List Pager */}
                    {this.state.articles.length > 0 &&
                        <ArticleListPager
                            totalPages={this.state.totalPages}
                            currentPage={this.state.page}
                            prevBtnHandler={this.prevPageHandler}
                            nextBtnHandler={this.nextPageHandler}
                            pageBtnHander={this.pageBtnHandler} />
                    }
                </div>
                <div className="listpage-col gutter" ref={(x) => this._adGutter = x}></div>
            </div>
        )
    }
}


// WEBPACK FOOTER //
// ./~/source-map-loader!./app/src/clp/category-landing-page.tsx